import type { TenantRole } from "@fts/core";
import type { IconName } from "../components/Icon";
import type { MessageKey } from "../i18n/tr";
import { buildMenu, type NavNode } from "./menu";

/**
 * Phone bottom bar (ADR 0030): the four screens each role opens most while walking the site, plus "Menu".
 * The preference is only an order: an entry is shown only if the same role's side menu has it, so the bar
 * can never offer a screen the role may not open (the route guards stay the single source of truth).
 */
export type BottomItemId = "overview" | "map" | "alarms" | "vehicles" | "checklistFaults" | "permissions";

export type BottomItem = { id: BottomItemId; href: string; icon: IconName; labelKey: MessageKey };

export const BOTTOM_ITEMS: Record<BottomItemId, Omit<BottomItem, "id">> = {
  overview: { href: "#/", icon: "home", labelKey: "bottomNav.overview" },
  map: { href: "#/map", icon: "map", labelKey: "bottomNav.map" },
  alarms: { href: "#/alarms", icon: "bell", labelKey: "bottomNav.alarms" },
  vehicles: { href: "#/vehicles", icon: "truck", labelKey: "bottomNav.vehicles" },
  checklistFaults: { href: "#/checklists/faults", icon: "alert", labelKey: "bottomNav.faults" },
  permissions: { href: "#/permissions", icon: "key", labelKey: "bottomNav.permissions" },
};

/** Most used screens per role, in bar order (audit, docs/design/ui-faz-c.md). */
export const BOTTOM_PREFERENCE: Record<TenantRole, readonly BottomItemId[]> = {
  admin: ["overview", "map", "alarms", "vehicles"],
  fleet_manager: ["overview", "map", "vehicles", "checklistFaults"],
  safety: ["overview", "alarms", "map", "permissions"],
  viewer: ["overview", "map", "alarms", "vehicles"],
};

export const BOTTOM_MAX = 4;

function menuIds(nodes: NavNode[], out = new Set<string>()): Set<string> {
  for (const n of nodes) {
    if (n.kind === "item") out.add(n.id);
    if (n.children) menuIds(n.children, out);
  }
  return out;
}

/** Bar entries for a role: its preference, filtered by what its own menu offers, at most BOTTOM_MAX. */
export function bottomItems(role: TenantRole, platformAdmin = false, preference: readonly BottomItemId[] = BOTTOM_PREFERENCE[role]): BottomItem[] {
  const allowed = menuIds(buildMenu([], role, platformAdmin));
  return preference
    .filter((id) => allowed.has(id))
    .slice(0, BOTTOM_MAX)
    .map((id) => ({ id, ...BOTTOM_ITEMS[id] }));
}

/** The bar entry that matches the current menu highlight (detail pages light up their list). */
export function bottomActive(items: BottomItem[], activeHref: string): BottomItemId | null {
  if (activeHref.startsWith("#/vehicles/")) return items.some((i) => i.id === "vehicles") ? "vehicles" : null;
  return items.find((i) => i.href === activeHref)?.id ?? null;
}
